import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '../auth/AuthProvider'
import { useCart } from '../cart/CartProvider'
import { useMarketShellScrollLock } from '../hooks/useMarketShellScrollLock'
import { useOrganizationAccess } from '../hooks/useOrganizationAccess'
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed'
import { VenueTabs } from '../venues/VenueTabs'
import { DemoStandBanner } from '../dev/DemoStandBanner'
import { DemoUiBanner } from '../dev/DemoUiBanner'
import { DEMO_STAND_REGISTER_URL, isDemoStandEnabled } from '../dev/demoStand'
import type { UserRole } from '../api/types'
import { NavIcon, type NavIconName } from './NavIcon'
import { SelectCombobox } from './SelectCombobox'
import { IconLogout } from './icons/CatalogActionIcons'
import { MenuIcon } from './icons/MenuIcon'
import { ReleaseVersion } from './ReleaseVersion'
import {
  IconChevronLeft,
  IconChevronRight,
  IconSidebarCollapse,
  IconSidebarExpand,
} from './icons/SidebarCollapseIcon'

type NavItem = {
  to: string
  label: string
  icon: NavIconName
  badge?: number
}

type NavGroup = {
  label?: string
  items: NavItem[]
}

/** Меню ресторана: «Мои прайсы» и всё вокруг них. */
const restaurantNav: NavGroup[] = [
  {
    items: [
      { to: '/my-prices', label: 'Мои прайсы', icon: 'prices' },
      { to: '/my-prices/suppliers', label: 'Мои поставщики', icon: 'suppliers' },
      { to: '/analytics', label: 'Динамика цен', icon: 'analytics' },
      { to: '/purchase', label: 'Заказ по остаткам', icon: 'purchase' },
      { to: '/cart', label: 'Заявка', icon: 'cart' },
      { to: '/settings', label: 'Настройки', icon: 'settings' },
    ],
  },
]

/** Кабинета поставщика нет — у прежних аккаунтов поставщиков остаются настройки. */
const supplierNav: NavGroup[] = [
  {
    items: [{ to: '/settings', label: 'Настройки', icon: 'settings' }],
  },
]

function navForRole(role: UserRole | null): NavGroup[] {
  const groups = role === 'supplier' ? supplierNav : restaurantNav
  if (!isDemoStandEnabled()) return groups

  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.to !== '/settings'),
    }))
    .filter((group) => group.items.length > 0)
}


/**
 * Какой пункт меню подсветить для текущего адреса.
 *
 * Подсвечивается самый длинный подходящий: на /my-prices/suppliers это «Поставщики»,
 * на /my-prices/import — «Мои прайсы», потому что своего пункта у импорта нет.
 *
 * Ни один из двух простых вариантов не годится. Без ограничения NavLink считает пункт
 * активным на всех вложенных адресах, и «Мои прайсы» подсвечивались заодно с
 * «Поставщиками». С end на всех пунктах пропадает подсветка на подстраницах — импорт
 * и карточка заявки остаются без раздела.
 */
const activeNavPath = (pathname: string, groups: NavGroup[]): string | null => {
  let best: string | null = null
  for (const group of groups) {
    for (const item of group.items) {
      const matches = pathname === item.to || pathname.startsWith(`${item.to}/`)
      if (matches && (best === null || item.to.length > best.length)) {
        best = item.to
      }
    }
  }
  return best
}

export const AppShell = () => {
  useMarketShellScrollLock()
  const { session, organizationName, role, organizations, logout, switchOrganization } = useAuth()
  const { lineCount } = useCart()
  const { ownerPaywallModal } = useOrganizationAccess()
  const { collapsed, toggleCollapsed } = useSidebarCollapsed()
  const [navOpen, setNavOpen] = useState(false)
  const currentOrg = organizations.find((org) => org.id === session?.organizationId)
  const displayName = isDemoStandEnabled()
    ? currentOrg?.legal_name || organizationName || session?.email || ''
    : organizationName || session?.email || ''
  const email = session?.email ?? ''
  const location = useLocation()
  const navGroups = navForRole(role)
  const activePath = activeNavPath(location.pathname, navGroups)

  const resolveBadge = (item: NavItem): number | undefined => {
    if (role === 'buyer' && item.to === '/cart' && lineCount > 0) {
      return lineCount
    }
    return item.badge
  }

  const closeNav = () => setNavOpen(false)

  const shellClass = [
    'market-shell',
    navOpen ? 'market-shell--nav-open' : '',
    collapsed ? 'market-shell--sidebar-collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={shellClass}>
      {navOpen ? (
        <button
          type="button"
          className="market-shell__backdrop"
          aria-label="Закрыть меню"
          onClick={closeNav}
        />
      ) : null}

      <button
        type="button"
        className="market-shell__mobile-toggle"
        aria-label={navOpen ? 'Закрыть меню' : 'Открыть меню'}
        aria-expanded={navOpen}
        onClick={() => setNavOpen((open) => !open)}
      >
        <MenuIcon />
      </button>

      <aside className="market-shell__sidebar" aria-label="Навигация">
        <div className="market-shell__brand">
          <span className="market-shell__logo" title="Провизиум">
            {collapsed ? 'П' : 'Провизиум'}
          </span>
          {!collapsed ? <span className="market-shell__subtitle">Личный кабинет</span> : null}
        </div>

        <nav id="market-shell-sidebar-nav" className="market-shell__nav">
          {navGroups.map((group) => (
            <div key={group.label ?? group.items[0]?.to} className="market-shell__nav-group">
              {group.label && !collapsed ? (
                <p className="market-shell__nav-label">{group.label}</p>
              ) : null}
              {group.items.map((item) => {
                const badge = resolveBadge(item)
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={item.label}
                    aria-label={item.label}
                    onClick={closeNav}
                    className={
                      item.to === activePath
                        ? 'market-shell__link market-shell__link--active'
                        : 'market-shell__link'
                    }
                  >
                    <NavIcon name={item.icon} />
                    <span className="market-shell__link-label">{item.label}</span>
                    {badge ? <span className="market-shell__badge">{badge}</span> : null}
                  </NavLink>
                )
              })}
            </div>
          ))}
        </nav>

        <div className="market-shell__user">
          {!collapsed ? (
            <>
              <p className="market-shell__user-name">{displayName || email}</p>
              {organizations.length > 1 ? (
                <SelectCombobox
                  className="market-shell__org-switch"
                  label="Организация"
                  hideLabel
                  value={String(session?.organizationId ?? '')}
                  options={organizations.map((org) => ({
                    value: String(org.id),
                    label: org.trade_name ?? org.legal_name,
                  }))}
                  onChange={(next) => void switchOrganization(Number(next))}
                />
              ) : organizationName && !isDemoStandEnabled() ? (
                <p className="market-shell__user-org">{organizationName}</p>
              ) : null}
            </>
          ) : null}
          {isDemoStandEnabled() ? (
            <a
              className="market-shell__demo-cta"
              href={DEMO_STAND_REGISTER_URL}
              target="_blank"
              rel="noreferrer"
              title="Зарегистрироваться и получить 30 дней бесплатно"
            >
              {collapsed ? (
                <span className="market-shell__demo-cta-compact">30 дней</span>
              ) : (
                <>
                  Зарегистрироваться
                  <span className="market-shell__demo-cta-sub">
                    30 дней бесплатно
                  </span>
                </>
              )}
            </a>
          ) : null}
          <button
            type="button"
            className="market-shell__collapse-toggle"
            aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            aria-expanded={!collapsed}
            aria-controls="market-shell-sidebar-nav"
            title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            onClick={toggleCollapsed}
          >
            {collapsed ? <IconSidebarExpand /> : <IconSidebarCollapse />}
            <span className="market-shell__collapse-toggle-label">
              {collapsed ? 'Развернуть' : 'Свернуть'}
            </span>
          </button>
          {isDemoStandEnabled() ? null : !collapsed ? (
            <button type="button" className="echat-btn echat-btn--ghost" onClick={logout}>
              Выйти
            </button>
          ) : (
            <button
              type="button"
              className="echat-btn echat-btn--ghost market-shell__icon-only-btn"
              onClick={logout}
              title="Выйти"
              aria-label="Выйти"
            >
              <IconLogout />
            </button>
          )}
          <ReleaseVersion />
        </div>

        <button
          type="button"
          className="market-shell__edge-toggle"
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          aria-expanded={!collapsed}
          aria-controls="market-shell-sidebar-nav"
          title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          onClick={toggleCollapsed}
        >
          {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>
      </aside>

      <main className="market-shell__main">
        <DemoUiBanner />
        <DemoStandBanner />
        {role === 'buyer' ? (
          <div className="market-shell__context-bar">
            <VenueTabs />
          </div>
        ) : null}
        <Outlet />
      </main>
      {ownerPaywallModal}
    </div>
  )
}
